// src/App.tsx
import React, { useState, useEffect } from 'react';
import WorkbenchLayout from './components/WorkbenchLayout';
import SimpleLoginPage from './components/SimpleLoginPage';
import ProfilePage from './components/ProfilePage';
import OrganizationPage from './components/OrganizationPage';
import AdminPanelPage from './components/AdminPanelPage';
import Marketplace from './components/Marketplace';
import GuaranteedOutputs from './components/GuaranteedOutputs';
import CategoryProducts from './components/CategoryProducts';
import ProductDetail from './components/ProductDetail';
import ActiveSessionsWarning from './components/ActiveSessionsWarning';
import NoProjectsTransition from './components/NoProjectsTransition';
import ProjectSelectionView from './components/ProjectSelectionView';
import ProjectWorkflowView, { WorkspaceBootstrap } from './components/ProjectWorkflowView';
import CreateProjectModal, { ProjectCreatedPayload } from './components/CreateProjectModal';
import TooltipManager from './components/TooltipManager';
import PendingOrgInvites from './components/PendingOrgInvites';
import { CapabilitiesProvider } from './contexts/CapabilitiesContext';
import { getCookie, NOTIFICATIONS_REFRESH_EVENT, requestNotificationsRefresh } from './utils/utils';
import { api } from './utils/client';
import { getPreferredOrgId, clearPreferredOrg } from './utils/preferredOrg';
import type { AppNotification } from './components/NotificationsPanel';

function researchStartedForProject(project: ProjectCreatedPayload): boolean {
  return (
    (Array.isArray(project.research_jobs) && project.research_jobs.length > 0) ||
    Boolean(project.research_job)
  );
}

interface UserData {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  profile_picture: string | null;
  preferred_currency?: string;
  org?: number | null;
  org_name?: string | null;
  default_org?: number | null;
  locks_to_default_org?: boolean;
  is_catalyx_user?: boolean;
  is_superuser?: boolean;
  is_staff?: boolean;
  is_free_tier?: boolean;
}

// --- REFACTORED: Added a simple welcome component ---
const WelcomePage: React.FC = () => (
  <div className="flex items-center justify-center h-full p-8">
    <div className="text-center">
      <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="text-3xl">👋</span>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">Welcome to Catalyx Canvas</h2>
      <p className="text-gray-600 mt-2">
        Select a project from the sidebar to view its blueprints, or create a new project to get started.
      </p>
    </div>
  </div>
);

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [activeNav, setActiveNav] = useState('');
  const [activeProject, setActiveProject] = useState<number | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('CHF');
  const [showActiveSessionsWarning, setShowActiveSessionsWarning] = useState(false);
  const [isResolvingOtherSessions, setIsResolvingOtherSessions] = useState(false);
  const [sessionWarningError, setSessionWarningError] = useState<string | null>(null);
  const [hasProjects, setHasProjects] = useState<boolean | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [workspaceProjectId, setWorkspaceProjectId] = useState<number | null>(null);
  const [workspaceBootstrap, setWorkspaceBootstrap] = useState<WorkspaceBootstrap | null>(null);
  const [showTooltipManager, setShowTooltipManager] = useState(false);
  const [showAiTester, setShowAiTester] = useState(false);
  const [projectsRefreshTrigger, setProjectsRefreshTrigger] = useState(0);
  const [marketplaceOverviewCategoryId, setMarketplaceOverviewCategoryId] = useState<number | null>(null);
  const [marketplaceOverviewProductId, setMarketplaceOverviewProductId] = useState<number | null>(null);

  // Shared project filters state (synced between sidebar and projects view)
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<number | null>(null);
  const [selectedMarketFilter, setSelectedMarketFilter] = useState<string>('');
  const [selectedTerritoryFilter, setSelectedTerritoryFilter] = useState<string>('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('');
  const [selectedOccasionFilter, setSelectedOccasionFilter] = useState<string>('');

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const fetchNotifications = async () => {
    if (!isLoggedIn) return;
    try {
      const data = await api<{ results: AppNotification[]; unread_count: number }>(
        '/api/notifications/'
      );
      setNotifications(Array.isArray(data.results) ? data.results : []);
      setUnreadCount(typeof data.unread_count === 'number' ? data.unread_count : 0);
    } catch {
      // Non-fatal — bell stays empty until next poll.
    }
  };

  // Load currency from user profile when user data is available
  useEffect(() => {
    if (userData && userData.preferred_currency) {
      setSelectedCurrency(userData.preferred_currency);
    }
  }, [userData]);

  useEffect(() => {
    if (!isLoggedIn) return;

    void fetchNotifications();

    const intervalId = window.setInterval(() => {
      void fetchNotifications();
    }, 45000);

    const handleFocus = () => {
      void fetchNotifications();
    };
    const handleRefreshEvent = () => {
      void fetchNotifications();
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, handleRefreshEvent);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, handleRefreshEvent);
    };
  }, [isLoggedIn]);

  // Save currency preference to backend when it changes
  const handleCurrencyChange = async (currency: string) => {
    // Update user's preferred currency on backend first
    if (isLoggedIn && userData) {
      try {
        const csrfToken = getCookie('csrftoken');
        const response = await fetch('/api/user/profile/', {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken || '',
          },
          body: JSON.stringify({
            preferred_currency: currency,
          }),
        });
        
        if (response.ok) {
          const updatedUser = await response.json();
          setUserData(updatedUser);
          // Update local state and refresh the page to show new currency
          setSelectedCurrency(currency);
          // Reload the page to refresh all product prices with new currency
          window.location.reload();
        }
      } catch (err) {
        console.error('Error updating currency preference:', err);
        // Still update local state even if backend save fails
        setSelectedCurrency(currency);
      }
    } else {
      // If not logged in, just update local state
      setSelectedCurrency(currency);
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    // ... (auth checking logic remains the same)
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    
    if (error) {
      handleAuthError(error);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      checkAuthStatus();
    }
  }, []);

  const handleAuthError = (error: string) => {
    // ... (error handling logic remains the same)
    switch (error) {
      case 'unauthorized':
        setAuthError('unauthorized');
        break;
      case 'no_code':
        setAuthError('Authentication failed: No authorization code received.');
        break;
      case 'no_token':
        setAuthError('Authentication failed: Could not retrieve access token.');
        break;
      case 'no_email':
        setAuthError('Authentication failed: Could not retrieve email from provider.');
        break;
      case 'oauth_failed':
        setAuthError('Authentication failed. Please try signing in again.');
        break;
      default:
        setAuthError('An unknown error occurred during authentication.');
    }
    setIsLoading(false);
  };

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/api/auth/check/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          const sessionCount = Number(data.other_sessions_count || 0);
          let user = data.user;
          if (user.locks_to_default_org) {
            clearPreferredOrg(user.id);
          } else {
            // Restore last selected org from localStorage on login/refresh.
            // Key is namespaced per user so a previous account cannot leak its org.
            const storedOrgId = getPreferredOrgId(user.id);
            if (storedOrgId && (!user.org || user.org !== parseInt(storedOrgId, 10))) {
              try {
                const csrfToken = getCookie('csrftoken');
                const patchRes = await fetch('/api/user/profile/', {
                  method: 'PATCH',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken || '',
                  },
                  body: JSON.stringify({ org: parseInt(storedOrgId, 10) }),
                });
                if (patchRes.ok) {
                  user = await patchRes.json();
                }
              } catch {
                // Ignore - use server org
              }
            }
          }
          setShowActiveSessionsWarning(sessionCount > 0);
          setSessionWarningError(null);
          setUserData(user);
          setHasProjects(data.has_projects ?? null);
          setIsLoggedIn(true);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = () => {
    setAuthError(null);
    checkAuthStatus();
  };

  const handleContinueAndLogoutOtherSessions = async () => {
    setSessionWarningError(null);
    setIsResolvingOtherSessions(true);
    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch('/api/auth/logout-other-sessions/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || data.error || 'Failed to log out other sessions');
      }
      setShowActiveSessionsWarning(false);
    } catch (error) {
      setSessionWarningError(error instanceof Error ? error.message : 'Failed to log out other sessions');
    } finally {
      setIsResolvingOtherSessions(false);
    }
  };

  const handleLogout = async () => {
    // ... (logout logic remains the same)
    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch('/api/auth/logout/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
      });

      if (response.ok) {
        clearPreferredOrg(userData?.id);
        setIsLoggedIn(false);
        setUserData(null);
        setAuthError(null);
        setShowActiveSessionsWarning(false);
        setSessionWarningError(null);
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleNavigate = (itemId: string) => {
    // Admin Panel is an overlay — keep current page/project intact.
    if (itemId === 'admin-panel') {
      setShowAdminPanel(true);
      return;
    }
    setActiveNav(itemId);
    // For Blueprints (empty string): 
    //   - If coming from logo click or explicit navigation, clear project to show projects view
    //   - If project is already selected, preserve it to show blueprints
    // For Marketplace: preserve activeProject
    // For other nav items: clear project selection
    if (itemId === '') {
      // Blueprints button clicked - only clear if no project selected
      // (If project is selected, we want to show its blueprints)
      // This will be handled by renderContent logic
    } else if (itemId !== 'marketplace' && itemId !== 'outcomes') {
      setActiveProject(null);
      setWorkspaceProjectId(null);
    }
    setSelectedCategoryId(null); // Clear category selection
    setSelectedProductId(null); // Clear product selection
    console.log('Navigating to:', itemId);
  };

  const handleProjectSelect = (projectId: number) => {
    // Handle clearing selection (when projectId is 0 or invalid)
    if (!projectId || projectId === 0) {
      setActiveProject(null);
      setWorkspaceProjectId(null);
      setActiveNav(''); // Navigate to projects view
      setSelectedCategoryId(null); // Clear marketplace navigation
      setSelectedProductId(null); // Clear marketplace navigation
      return;
    }
    
    setActiveProject(projectId);
    if (activeNav !== 'outcomes') {
      setWorkspaceProjectId(projectId);
    }
    // Keep current view when on outcomes (so selecting a project shows its outcomes)
    if (activeNav !== '' && activeNav !== 'outcomes') {
      setActiveNav(''); // Navigate to projects view if coming from elsewhere
    }
    setSelectedCategoryId(null); // Clear category selection
    setSelectedProductId(null); // Clear product selection
    console.log('Selected project:', projectId);
  };

  const handleViewGuaranteedOutputs = (projectId: number) => {
    setActiveProject(projectId);
    setActiveNav('outcomes');
    setSelectedCategoryId(null);
    setSelectedProductId(null);
  };

  const handleSecretProjectSeeded = (payload: {
    projectId: number;
    name?: string;
    recipeBlueprintId?: number;
    recipeCombinationId?: number;
  }) => {
    setHasProjects(true);
    setActiveProject(payload.projectId);
    setWorkspaceProjectId(payload.projectId);
    setWorkspaceBootstrap({
      project: {
        id: payload.projectId,
        name: payload.name || 'Raja',
        opportunity_space: '__CATALYX_SECRET_DEMO_V1__',
      },
      blueprintId: payload.recipeBlueprintId,
      recipeFocus: payload.recipeBlueprintId
        ? {
            step: 3,
            combinationId: payload.recipeCombinationId,
            recipePageView: 'recipe-card',
          }
        : null,
    });
    setActiveNav('');
    setProjectsRefreshTrigger((prev) => prev + 1);
    setSelectedCategoryId(null);
    setSelectedProductId(null);
  };

  const handleNavigateToMarketplaceProduct = (categoryId: number, productId: number) => {
    setActiveNav('marketplace');
    setSelectedCategoryId(null);
    setSelectedProductId(null);
    setMarketplaceOverviewCategoryId(categoryId);
    setMarketplaceOverviewProductId(productId);
  };

  const handleCategorySelect = (categoryId: number) => {
    setSelectedCategoryId(categoryId);
    // Preserve activeProject when selecting a category
    setSelectedProductId(null); // Clear product selection
    setActiveNav('marketplace'); // Keep marketplace nav active
  };

  const handleBackToMarketplace = () => {
    setSelectedCategoryId(null);
    setSelectedProductId(null);
  };

  const handleProductSelect = (productId: number) => {
    setSelectedProductId(productId);
    // Preserve activeProject when selecting a product
    setActiveNav('marketplace'); // Keep marketplace nav active
  };

  const handleBackToCategory = () => {
    setSelectedProductId(null);
  };

  const handleProjectDeleted = (projectId: number) => {
    setProjectsRefreshTrigger((prev) => prev + 1);
    if (activeProject === projectId) {
      setActiveProject(null);
      setWorkspaceProjectId(null);
    }
  };

  const handleProjectsMutated = () => {
    setProjectsRefreshTrigger((prev) => prev + 1);
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await api('/api/notifications/mark-all-read/', { method: 'POST' });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  };

  const handleOpenNotification = async (notification: AppNotification) => {
    if (!notification.project) return;

    try {
      if (!notification.is_read) {
        await api(`/api/notifications/${notification.id}/mark-read/`, { method: 'POST' });
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      }
    } catch {
      // continue navigation even if mark-read fails
    }

    setNotificationsOpen(false);
    setHasProjects(true);
    setActiveProject(notification.project);
    setWorkspaceProjectId(notification.project);

    let projectPayload: ProjectCreatedPayload = {
      id: notification.project,
      name: notification.project_name || `Project #${notification.project}`,
    };
    try {
      const fullProject = await api<ProjectCreatedPayload>(
        `/api/projects/${notification.project}/`
      );
      projectPayload = {
        ...fullProject,
        id: fullProject.id ?? notification.project,
        name:
          fullProject.name ||
          notification.project_name ||
          `Project #${notification.project}`,
        markets: Array.isArray(fullProject.markets) ? fullProject.markets : [],
      };
    } catch {
      // Fall back to id + name if the project fetch fails.
    }

    if (notification.kind === 'recipe_card') {
      setWorkspaceBootstrap({
        project: projectPayload,
        blueprintId: notification.blueprint ?? undefined,
        recipeFocus: notification.blueprint
          ? {
              step: 3,
              recipePageView: 'recipe-card',
              combinationId: notification.combination ?? undefined,
            }
          : null,
      });
    } else if (notification.kind === 'concept') {
      setWorkspaceBootstrap({
        project: projectPayload,
        blueprintId: notification.blueprint ?? undefined,
        recipeFocus: notification.blueprint
          ? {
              step: 3,
              recipePageView: 'product-idea',
              combinationId: notification.combination ?? undefined,
              artifactId: notification.artifact ?? undefined,
            }
          : null,
      });
    } else if (notification.kind === 'jtbd' || notification.kind === 'tensions') {
      setWorkspaceBootstrap({
        project: projectPayload,
        blueprintId: notification.blueprint ?? undefined,
        workspaceTabRequest: 'jobs-to-be-done',
        highlightedJobText: notification.job_to_be_done || undefined,
      });
    } else if (notification.kind === 'research') {
      setWorkspaceBootstrap({
        project: projectPayload,
      });
    } else {
      setWorkspaceBootstrap({
        project: projectPayload,
        blueprintId: notification.blueprint ?? undefined,
        focusPlatformId: notification.blueprint ?? undefined,
        workspaceTabRequest: 'innovation-platform',
      });
    }

    setActiveNav('');
    setSelectedCategoryId(null);
    setSelectedProductId(null);
  };

  const handleProjectContinue = (project: ProjectCreatedPayload) => {
    setHasProjects(true);
    setActiveProject(project.id);
    setWorkspaceProjectId(project.id);
    setWorkspaceBootstrap({
      project,
      drawerStep: 'platform-form',
    });
    setShowCreateModal(false);
    setProjectsRefreshTrigger((prev) => prev + 1);
    if (researchStartedForProject(project)) {
      requestNotificationsRefresh();
    }
  };

  const handleProjectCreatedFromTransition = (project: ProjectCreatedPayload) => {
    setHasProjects(true);
    setShowCreateModal(false);
    setProjectsRefreshTrigger((prev) => prev + 1);
    if (project.lifecycle === 'draft') {
      return;
    }
    setActiveProject(project.id);
    setWorkspaceProjectId(project.id);
    if (researchStartedForProject(project)) {
      requestNotificationsRefresh();
    }
  };

  const renderWorkspace = () =>
    workspaceProjectId == null ? null : (
      <ProjectWorkflowView
        projectId={workspaceProjectId}
        workspaceBootstrap={workspaceBootstrap}
        onBootstrapConsumed={() => setWorkspaceBootstrap(null)}
        onNavigateToMarketplaceProduct={handleNavigateToMarketplaceProduct}
        onClose={() => {
          setWorkspaceProjectId(null);
          setWorkspaceBootstrap(null);
        }}
      />
    );

  const keepWorkspaceMounted =
    workspaceProjectId != null && (activeNav === '' || activeNav === 'marketplace');
  const workspaceVisible =
    keepWorkspaceMounted &&
    activeNav === '' &&
    selectedCategoryId == null &&
    selectedProductId == null;

  const renderContent = () => {
    // If a product is selected, show its detail page (highest priority)
    if (selectedProductId !== null) {
      return (
        <ProductDetail
          productId={selectedProductId}
          onBack={handleBackToCategory}
          selectedCurrency={selectedCurrency}
          activeProject={activeProject}
          onProjectCreated={() => {
            setProjectsRefreshTrigger(prev => prev + 1); // Trigger sidebar refresh
          }}
        />
      );
    }

    // If a category is selected, show its products
    if (selectedCategoryId !== null) {
      return (
        <CategoryProducts
          categoryId={selectedCategoryId}
          onBack={handleBackToMarketplace}
          onProductSelect={handleProductSelect}
          selectedCurrency={selectedCurrency}
        />
      );
    }

    // Show pages based on main navigation
    switch (activeNav) {
      case 'profile':
        return <ProfilePage />;
      case 'organization':
        return <OrganizationPage />;
      case 'marketplace':
        return (
          <Marketplace
            onCategorySelect={handleCategorySelect}
            selectedCurrency={selectedCurrency}
            initialOverviewCategoryId={marketplaceOverviewCategoryId}
            initialProductId={marketplaceOverviewProductId}
            onInitialSelectionConsumed={() => {
              setMarketplaceOverviewCategoryId(null);
              setMarketplaceOverviewProductId(null);
            }}
          />
        );
      case 'outcomes':
        if (activeProject !== null) {
          return (
            <GuaranteedOutputs
              projectId={activeProject}
              isCatalyxUser={!!userData?.is_catalyx_user}
            />
          );
        }
        return (
          <ProjectSelectionView
            onProjectSelect={handleProjectSelect}
            onViewGuaranteedOutputs={handleViewGuaranteedOutputs}
            onCreateProject={() => setShowCreateModal(true)}
            selectedTeamFilter={selectedTeamFilter}
            setSelectedTeamFilter={setSelectedTeamFilter}
            selectedMarketFilter={selectedMarketFilter}
            setSelectedMarketFilter={setSelectedMarketFilter}
            selectedTerritoryFilter={selectedTerritoryFilter}
            setSelectedTerritoryFilter={setSelectedTerritoryFilter}
            selectedCategoryFilter={selectedCategoryFilter}
            setSelectedCategoryFilter={setSelectedCategoryFilter}
            selectedOccasionFilter={selectedOccasionFilter}
            setSelectedOccasionFilter={setSelectedOccasionFilter}
            onProjectDeleted={handleProjectDeleted}
            onProjectRenamed={handleProjectsMutated}
            refreshTrigger={projectsRefreshTrigger}
          />
        );
      default:
        if (workspaceProjectId !== null) {
          return null;
        }
        return (
          <ProjectSelectionView
            onProjectSelect={handleProjectSelect}
            onViewGuaranteedOutputs={handleViewGuaranteedOutputs}
            onCreateProject={() => setShowCreateModal(true)}
            selectedTeamFilter={selectedTeamFilter}
            setSelectedTeamFilter={setSelectedTeamFilter}
            selectedMarketFilter={selectedMarketFilter}
            setSelectedMarketFilter={setSelectedMarketFilter}
            selectedTerritoryFilter={selectedTerritoryFilter}
            setSelectedTerritoryFilter={setSelectedTerritoryFilter}
            selectedCategoryFilter={selectedCategoryFilter}
            setSelectedCategoryFilter={setSelectedCategoryFilter}
            selectedOccasionFilter={selectedOccasionFilter}
            setSelectedOccasionFilter={setSelectedOccasionFilter}
            onProjectDeleted={handleProjectDeleted}
            onProjectRenamed={handleProjectsMutated}
            refreshTrigger={projectsRefreshTrigger}
          />
        );
    }
  };

  // Show loading state
  if (isLoading) {
    // ... (loading spinner remains the same)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 via-white to-blue-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // --- (All auth error/access denied logic remains the same) ---

  // Show unauthorized access message
  if (authError === 'unauthorized') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 via-white to-blue-100">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            {/* ... (Access Denied content) ... */}
          </div>
        </div>
      </div>
    );
  }

  // Show other auth errors
  if (authError && authError !== 'unauthorized') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 via-white to-blue-100">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Sign-in issue</h2>
            <p className="text-sm text-gray-600 mb-6">{authError}</p>
            <button
              type="button"
              onClick={() => {
                setAuthError(null);
                window.location.href = '/api/auth/login/';
              }}
              className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show login page if not logged in
  if (!isLoggedIn) {
    return <SimpleLoginPage onLogin={handleLogin} />;
  }

  if (showActiveSessionsWarning) {
    return (
      <ActiveSessionsWarning
        isSubmitting={isResolvingOtherSessions}
        error={sessionWarningError}
        onContinueAndLogoutOthers={handleContinueAndLogoutOtherSessions}
      />
    );
  }

  // Show no-projects transition screen if user has zero projects
  if (
    hasProjects === false &&
    activeNav !== 'marketplace' &&
    activeNav !== 'profile' &&
    activeNav !== 'organization'
  ) {
    return (
      <CapabilitiesProvider
        orgId={userData?.org ?? undefined}
        grantAllCapabilities={Boolean(userData?.is_superuser || userData?.is_staff)}
      >
        <NoProjectsTransition
          onGoToMarketplace={() => {
            setActiveNav('marketplace');
            // Force re-render into workbench mode
            setHasProjects(true);
          }}
          onCreateProject={() => setShowCreateModal(true)}
        />
        {showCreateModal && (
          <CreateProjectModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onProjectContinue={handleProjectContinue}
            onProjectCreated={handleProjectCreatedFromTransition}
          />
        )}
      </CapabilitiesProvider>
    );
  }

  // Map current view to tooltip page_key for resolution-independent placed tooltips
  const tooltipPageKey =
    activeNav === 'tooltips'
      ? null
      : activeNav === ''
        ? activeProject != null
          ? 'blueprint-builder'
          : 'project-list'
        : activeNav === 'marketplace'
          ? 'marketplace'
          : activeNav === 'outcomes'
            ? 'outcomes'
            : activeNav === 'profile'
              ? 'profile'
              : activeNav === 'organization'
                ? 'organization'
                  : null;

  // Show main workbench layout after login
  return (
    <CapabilitiesProvider
      orgId={userData?.org ?? undefined}
      grantAllCapabilities={Boolean(userData?.is_superuser || userData?.is_staff)}
    >
      <WorkbenchLayout
        activeProject={activeProject}
        activeNav={activeNav}
        onNavigate={handleNavigate}
        onProjectSelect={handleProjectSelect}
        onProjectContinue={handleProjectContinue}
        onLogout={handleLogout}
        userData={userData}
        selectedCurrency={selectedCurrency}
        onCurrencyChange={handleCurrencyChange}
        projectsRefreshTrigger={projectsRefreshTrigger}
        onProjectsChanged={handleProjectsMutated}
        tooltipPageKey={tooltipPageKey}
        showTooltipManager={showTooltipManager}
        onCloseTooltipManager={() => setShowTooltipManager(false)}
        onOpenTooltipManager={() => setShowTooltipManager(true)}
        showAiTester={showAiTester}
        onCloseAiTester={() => setShowAiTester(false)}
        onOpenAiTester={() => setShowAiTester(true)}
        projectSearch={projectSearch}
        setProjectSearch={setProjectSearch}
        selectedTeamFilter={selectedTeamFilter}
        setSelectedTeamFilter={setSelectedTeamFilter}
        selectedMarketFilter={selectedMarketFilter}
        setSelectedMarketFilter={setSelectedMarketFilter}
        selectedTerritoryFilter={selectedTerritoryFilter}
        setSelectedTerritoryFilter={setSelectedTerritoryFilter}
        selectedCategoryFilter={selectedCategoryFilter}
        setSelectedCategoryFilter={setSelectedCategoryFilter}
        selectedOccasionFilter={selectedOccasionFilter}
        setSelectedOccasionFilter={setSelectedOccasionFilter}
        onSecretProjectSeeded={handleSecretProjectSeeded}
        notifications={notifications}
        unreadCount={unreadCount}
        notificationsOpen={notificationsOpen}
        onToggleNotifications={() => setNotificationsOpen((open) => !open)}
        onCloseNotifications={() => setNotificationsOpen(false)}
        onOpenNotification={handleOpenNotification}
        onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
      >
        <PendingOrgInvites />
        {keepWorkspaceMounted && (
          <div className={workspaceVisible ? 'h-full min-h-0 flex flex-col' : 'hidden'}>
            {renderWorkspace()}
          </div>
        )}
        {!workspaceVisible && renderContent()}
      </WorkbenchLayout>
      {showCreateModal && (
        <CreateProjectModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onProjectContinue={handleProjectContinue}
          onProjectCreated={handleProjectCreatedFromTransition}
        />
      )}
      {showAdminPanel && (
        <AdminPanelPage onClose={() => setShowAdminPanel(false)} />
      )}
    </CapabilitiesProvider>
  );
}